'use client';

import { useChiliPiper } from '@/app/(intakeForm)/sign-up/get-an-estimate/hooks/useChiliPiper';
import { LoadingSpinnerIcon } from '@/assets/icons/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { ErrorModal } from '@/components/ui/ErrorModal';
import { devLog } from '@/lib/logging';
import { cn } from '@/lib/ui/cn';
import type { BlockContactFormProps } from '@/types/blocks';
import type {
  ContactFormFormSortAnswers,
  ContactFormFormSortEvent,
  ContactFormTypeB2C,
} from '@/types/forms';
import { pushFormEventToDataLayer, toDataLayerData } from '@/utils/dataLayer';
import { extractErrorMessage } from '@/utils/http-error-messages';
import { TrackingHelper, type TrackingData } from '@/utils/tracking';
import EmbedFlow from '@formsort/react-embed';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useSubmitContactForm } from './useSubmitContactForm';

const FORM_TYPE_DEFAULT: ContactFormTypeB2C = 'contact-us';

type FormState = 'idle' | 'submitting' | 'scheduling' | 'redirecting' | 'error';

const ContactFormContainer = ({
  children,
  anchorId,
}: {
  children: React.ReactNode;
  anchorId?: string;
}) => {
  return (
    <div id={anchorId} className='w-full'>
      {children}
    </div>
  );
};

const handleChiliPiperScheduling = async ({
  openScheduler,
  answers,
  trackingData,
  thankYouPagePath,
}: {
  openScheduler: ReturnType<typeof useChiliPiper>['openScheduler'];
  answers: ContactFormFormSortAnswers;
  trackingData: TrackingData;
  thankYouPagePath: string;
}) => {
  // Prepare ChiliPiper payload
  const chiliPayload = {
    email: answers.email || '',
    firstName: answers.first_name || '',
    lastName: answers.last_name || '',
    phone: answers.phone || '',
    postalCode: answers.postal_code || '',
    source: 'Contact Form',
    note: answers.note || '',
    metadata: { ...trackingData },
  };

  // Pass thankYouPagePath as dynamicRedirectLink
  const redirectUrl = window.location.origin + thankYouPagePath;

  await openScheduler(
    chiliPayload,
    { dynamicRedirectLink: redirectUrl },
    url => {
      const redirectUrl = new URL(window.location.origin + thankYouPagePath);

      redirectUrl.searchParams.set('chilipiper', url);

      window.location.href = redirectUrl.href;
    }
  );
};

const REDIRECT_TIMEOUT = 3000;

export const ContactForm = ({
  client,
  flow,
  variant,
  formType = 'contact-us',
  thankYouPagePath = '/forms/contact-us/thank-you',
  drawerCta = '',
  anchorId = undefined,
  locationDefault = undefined,
  onSuccess,
  onError,
  ...rest
}: BlockContactFormProps) => {
  const [formState, setFormState] = useState<FormState>('idle');
  const [showForm, setShowForm] = useState(!drawerCta);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastAnswers, setLastAnswers] =
    useState<ContactFormFormSortAnswers | null>(null);
  const router = useRouter();
  const t = useTranslations('ContactUsPage');
  const tHttp = useTranslations('HttpErrors');
  const { openScheduler, isLoading: isChiliLoading } = useChiliPiper();

  const { submit: submitContactForm } = useSubmitContactForm();

  const handleFlowLoaded = () => {
    devLog('Contact form loaded');
  };

  if (!client || !flow || !variant) {
    throw new Error(
      'client, flow, and variant props are required for ContactForm component.'
    );
  }

  useEffect(() => {
    if (formState === 'redirecting') {
      setTimeout(() => {
        const url = new URL(window.location.href);
        const redirectUrl = url.origin + thankYouPagePath;
        router.push(redirectUrl);
      }, REDIRECT_TIMEOUT);
    }
  }, [formState, thankYouPagePath, router]);

  const handleShowForm = () => {
    setShowForm(true);
  };

  const closeErrorModal = () => {
    setFormState('idle');
    setSubmitError(null);
    if (anchorId) {
      // Scroll to the form container when error is closed
      setTimeout(() => {
        document
          .getElementById(anchorId)
          ?.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }
  };

  const handleFlowFinalized = async (data: ContactFormFormSortEvent) => {
    devLog('Contact form finalized:', data);
    setFormState('submitting');
    setSubmitError(null);

    try {
      // Safely extract answers from the flow state
      const answers =
        data && data.answers
          ? (data.answers as ContactFormFormSortAnswers)
          : ({} as ContactFormFormSortAnswers);

      // Store answers for potential repopulation
      setLastAnswers(answers);

      const trackingData = TrackingHelper.getTrackingData();

      const payload = {
        ...answers,
        ...trackingData,
        location_default: locationDefault,
        company:
          answers.form_type === FORM_TYPE_DEFAULT
            ? `${answers.first_name}, ${answers.last_name} Family`
            : answers.company,
        ContactUsType: 'General',
      } as const;

      try {
        pushFormEventToDataLayer({
          eventName: 'FlowFinalized',
          form_type: 'contact_us',
          data: toDataLayerData(payload),
        });
      } catch (error) {
        console.error('Error sending data to dataLayer:', error);
      }

      const response = await submitContactForm(payload);

      if (response.ok) {
        const result = await response.json();
        devLog('Contact form submitted successfully:', result);
        onSuccess?.();
        setFormState('scheduling');

        if (answers.form_type === 'contact-us') {
          await handleChiliPiperScheduling({
            openScheduler,
            answers,
            trackingData,
            thankYouPagePath,
          });
        }

        setFormState('redirecting');
      } else {
        const errorMessage = await extractErrorMessage(
          response,
          (key: string) => tHttp(key.replace('HttpErrors.', '')),
          t('error.defaultMessage')
        );

        setSubmitError(errorMessage);
        setFormState('error');
        onError?.(response);
      }
    } catch (error) {
      console.error('Unexpected error submitting contact form:', error);
      const errorMessage = t('error.unexpectedError');
      setSubmitError(errorMessage);
      setFormState('error');
      onError?.(error);
    }
  };

  // Show loading spinner when submitting or redirecting
  if (
    formState === 'submitting' ||
    formState === 'redirecting' ||
    formState === 'scheduling' ||
    isChiliLoading
  ) {
    const message =
      formState === 'scheduling' || isChiliLoading
        ? t('scheduling')
        : formState === 'redirecting'
          ? t('redirecting')
          : t('submitting');

    return (
      <div className='inset-0 z-50 flex items-center justify-center'>
        <div className='flex flex-col items-center space-y-4'>
          <LoadingSpinnerIcon className='text-sh-sky size-16' />
          <p className='text-sh-dark-gray'>{message}</p>
        </div>
      </div>
    );
  }

  // Show CTA button if drawerCta has text and form hasn't been shown yet
  if (drawerCta && !showForm) {
    return (
      <ContactFormContainer anchorId={anchorId}>
        <div className={cn('bg-sh-paper flex justify-center py-8')} {...rest}>
          <Button variant='dark' onClick={handleShowForm}>
            {drawerCta}
          </Button>
        </div>
      </ContactFormContainer>
    );
  }

  return (
    <ContactFormContainer anchorId={anchorId}>
      {(formState === 'idle' || formState === 'error') && (
        <div className={cn('formsort formsort__container')} {...rest}>
          <EmbedFlow
            clientLabel={client}
            flowLabel={flow}
            variantLabel={variant}
            queryParams={[
              ['form_type', formType],
              // Repopulate form with last answers if there was an error
              ...Object.entries(lastAnswers ?? {}).map(
                ([key, value]) => [key, String(value)] as [string, string]
              ),
            ]}
            embedConfig={{
              autoHeight: true,
              style: {
                height: '100%',
                width: '100%',
              },
            }}
            onFlowLoaded={handleFlowLoaded}
            onFlowClosed={() => {
              devLog('Contact form closed');
            }}
            onFlowFinalized={handleFlowFinalized}
            onStepLoaded={step => {
              devLog('Contact form step loaded:', step);
            }}
            onStepCompleted={step => {
              devLog('Contact form step completed:', step);
            }}
            onRedirect={() => ({ cancel: true })}
            onUnauthorized={() => {
              devLog('Contact form unauthorized');
            }}
          />
        </div>
      )}

      <ErrorModal
        isOpen={formState === 'error'}
        onClose={closeErrorModal}
        error={submitError || ''}
        title={t('error.title')}
      />
    </ContactFormContainer>
  );
};
